// dashboard/src/components/api-keys/WelcomeToApiKeysCard.tsx
import React from 'react';
import { useNavigate } from 'react-router-dom';

interface WelcomeToApiKeysCardProps {
  onCreateFirstKey: () => void;
}

const WelcomeToApiKeysCard: React.FC<WelcomeToApiKeysCardProps> = ({ onCreateFirstKey }) => {
  const navigate = useNavigate();

  const handleTryPlayground = () => {
    navigate('/dashboard/api-playground', { 
      state: { 
        showWelcome: true,
        fromApiKeys: true 
      } 
    });
  };

  return (
    <div className="bg-gradient-to-r from-green-50 to-emerald-100 border border-green-200 rounded-lg p-8 mb-6">
      <div className="text-center">
        <div className="flex justify-center mb-4">
          <div className="bg-green-500 rounded-full p-3">
            <span className="text-white text-3xl">🔑</span>
          </div>
        </div>
        
        <h2 className="text-2xl font-bold text-green-900 mb-3">
          Create Your First API Key
        </h2>
        
        <p className="text-green-800 text-lg mb-6 max-w-2xl mx-auto">
          You're all set with your subscription! Now let's create your first API key so you can start making requests to our powerful web crawling API.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="bg-white rounded-lg p-4 border border-green-200">
            <div className="text-green-500 text-2xl mb-2">🔒</div>
            <h3 className="font-semibold text-green-900 mb-1">Secure Authentication</h3>
            <p className="text-sm text-green-700">Your API key authenticates all requests securely</p>
          </div>
          
          <div className="bg-white rounded-lg p-4 border border-green-200">
            <div className="text-blue-500 text-2xl mb-2">📊</div>
            <h3 className="font-semibold text-green-900 mb-1">Usage Tracking</h3>
            <p className="text-sm text-green-700">Monitor your API usage and credit consumption</p>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 border border-green-200 mb-6">
          <h3 className="text-lg font-semibold text-green-900 mb-4">What's Next?</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-left">
            <div className="flex items-start">
              <span className="text-green-500 mr-3 mt-1">1️⃣</span>
              <div>
                <p className="font-medium text-green-900">Create API Key</p>
                <p className="text-sm text-green-700">Generate your secure access token</p>
              </div>
            </div>
            <div className="flex items-start">
              <span className="text-blue-500 mr-3 mt-1">2️⃣</span>
              <div>
                <p className="font-medium text-green-900">Test in Playground</p>
                <p className="text-sm text-green-700">Try your first API request</p>
              </div>
            </div>
            <div className="flex items-start">
              <span className="text-purple-500 mr-3 mt-1">3️⃣</span>
              <div>
                <p className="font-medium text-green-900">Start Building</p>
                <p className="text-sm text-green-700">Integrate into your projects</p>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <button
            onClick={onCreateFirstKey}
            className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-green-700 hover:to-emerald-700 transition-all duration-200 transform hover:scale-105 shadow-lg text-lg"
          >
            🚀 Create My First API Key
          </button>
          
          <button
            onClick={handleTryPlayground}
            className="w-full bg-blue-100 text-blue-700 py-2 px-4 rounded-lg font-medium hover:bg-blue-200 transition-colors"
          >
            🛠️ Explore API Playground First
          </button>
        </div>

        <div className="mt-6 p-4 bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-lg">
          <div className="flex items-center justify-center mb-2">
            <span className="text-yellow-500 text-lg mr-2">💡</span>
            <h4 className="font-semibold text-yellow-900">Pro Tip</h4>
          </div>
          <p className="text-sm text-yellow-800">
            You can create multiple API keys for different projects or environments. Each key can be managed independently.
          </p>
        </div>
      </div>
    </div>
  );
};

export default WelcomeToApiKeysCard;
